import i18n from '../config/i18next';

/**
 * Union of all translatable route keys.
 * Each key maps to a `routes.<key>` entry in the i18n translation files.
 * Adding a new language requires only a new translations file — no code change here.
 *
 * Not included: brand names (emb, medeis, saga-microbiotes, au-coeur-du-vivant)
 * and article prefixes (article-emb, article-medeis) whose slugs are already
 * managed by Strapi's localization graph.
 */
export type RouteKey =
  | 'espace-prive'
  | 'parcours-formation'
  | 'bibliotheque-savoir'
  | 'brique-de-savoir'
  | 'test-orientation'
  | 'mon-profil'
  | 'profil'
  | 'favoris'
  | 'informations-personnelles'
  | 'contacter-pileje'
  | 'evenements'
  | 'plan-du-site'
  | 'inscription'
  | 'inscription-doublon'
  | 'confirmation'
  | 'erreur'
  | 'non-disponible'
  | 'resultat';

// Runtime array of all RouteKey values, used for reverse lookup in getRouteKeyFromSlug.
const ROUTE_KEYS: RouteKey[] = [
  'espace-prive',
  'parcours-formation',
  'bibliotheque-savoir',
  'brique-de-savoir',
  'test-orientation',
  'mon-profil',
  'profil',
  'favoris',
  'informations-personnelles',
  'contacter-pileje',
  'evenements',
  'plan-du-site',
  'inscription',
  'inscription-doublon',
  'confirmation',
  'erreur',
  'non-disponible',
  'resultat'
];

/**
 * Returns the translated URL segment for a given route key and locale.
 * Relies on i18next getFixedT so it can be called outside of React components
 * (e.g. route registration in App.tsx, user service redirect).
 * Falls back to the default locale (French) if the requested locale has no translation,
 * and falls back to the key itself if the translation entry is missing entirely.
 * @param locale
 * @param key
 * @returns
 */
export function getRouteSlug(locale: string, key: RouteKey): string {
  const result = i18n.getFixedT(locale)(`routes.${key}`);
  return typeof result === 'string' ? result : key;
}

/**
 * Reverse lookup: given a URL segment in a specific locale, returns the corresponding
 * RouteKey, or undefined if the segment does not match any translatable route.
 * Used by LanguageSelector to map the current URL back to neutral keys before
 * rebuilding the path in the target locale.
 * @param locale
 * @param slug
 * @returns
 */
export function getRouteKeyFromSlug(locale: string, slug: string): RouteKey | undefined {
  return ROUTE_KEYS.find((key) => getRouteSlug(locale, key) === slug);
}

/**
 * Translates every translatable segment of a path from one locale to another.
 * Segments that don't match a known RouteKey (dynamic content slugs, brand names)
 * are left untouched. A trailing query string is preserved as-is.
 * @param fromLocale locale the path's segments are currently expressed in
 * @param toLocale locale to translate the segments into
 * @param path
 * @returns
 */
export function translateRoutePath(fromLocale: string, toLocale: string, path: string): string {
  const [pathname, query] = path.split('?');
  const translatedPathname = pathname
    .split('/')
    .map((segment) => {
      const key = getRouteKeyFromSlug(fromLocale, segment);
      return key ? getRouteSlug(toLocale, key) : segment;
    })
    .join('/');
  return query ? `${translatedPathname}?${query}` : translatedPathname;
}
